import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MultiSelectDropdown from "../EnhanceFilters/MultiSelectDropdown";
import { mapFiltersToPayload } from "./utils/mapDashboardFilters";
import { fetchFilterOptions, fetchWorkflowDashboard, setDashboardFilters, setFilterOptionSearch } from "./redux/WorkflowDashboardSlicer";
import { useDispatch, useSelector } from "react-redux";
import Chip from "@mui/material/Chip"; // add to your MUI imports
import "../pages/EnhanceWorkflow/EnhanceWorkflowFilter/EnhanceWorkflowfilter.css"; // adjust relative path to wherever it actually lives

const FILTER_GROUP_LABELS = {
  facility: "Facility",
  speciality: "Specialty",
  coder: "Coder",
  principalDiagnosis: "Principal Diagnosis",
  drg: "DRG",
  healthFund: "Health Fund",
  episodeStatus: "Episode Status",
  episodeId: "Episode ID",
  auditRuleCategory: "Audit Rule Category",
};

// Flattens filters state into a list of removable chips
function buildSelectedChips(filters) {
  const chips = [];

  Object.entries(FILTER_GROUP_LABELS).forEach(([key, groupLabel]) => {
    const val = filters[key];
    if (Array.isArray(val)) {
      val.forEach((item) => {
        const text = typeof item === "string" ? item : item?.label ?? String(item);
        chips.push({ groupKey: key, groupLabel, itemValue: item, text: `${groupLabel}: ${text}` });
      });
    }
  });

  if (filters.admissionFrom || filters.admissionTo) {
    chips.push({
      groupKey: "admissionDate",
      isRange: true,
      text: `Admission: ${filters.admissionFrom || "…"} → ${filters.admissionTo || "…"}`,
    });
  }
  if (filters.separationFrom || filters.separationTo) {
    chips.push({
      groupKey: "separationDate",
      isRange: true,
      text: `Separation: ${filters.separationFrom || "…"} → ${filters.separationTo || "…"}`,
    });
  }
  if (filters.losOp && filters.los) {
    chips.push({
      groupKey: "los",
      isRange: true,
      text: `LOS ${filters.losOp} ${filters.los}`,
    });
  }

  return chips;
}

const BORDER = "1px solid #E2E8F0";
const TEXT_MUTED = "#64748B";
const TEXT_HEADING = "#1E293B";
const BRAND_BLUE = "#1565C0";
// Add this after the imports
const operatorOptions = [">", "<", ">=", "<=", "=", "!="];
// TODO: replace these with real option lists (from API / constants file)
const facilityOptions = [];
const specialityOptions = [];
const principalDiagnosisOptions = [];
const drgOptions = [];
const healthFundOptions = [];
const episodeStatusOptions = [];
const coderOptions = [];
const episodeIdOptions = [];
const auditStatusOptions = [
  { label: "Pending Review", value: "Pending Review" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];
const auditRuleCategoryOptions = [];

function SectionLabel({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box
        sx={{ width: 3, height: 14, bgcolor: BRAND_BLUE, borderRadius: 1 }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: TEXT_HEADING,
          letterSpacing: 0.3,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function FieldLabel({ children }) {
  return (
    <Typography sx={{ fontSize: 12, color: TEXT_MUTED, mb: 0.5 }}>
      {children}
    </Typography>
  );
}
// A tiny reusable hook so each dropdown doesn't repeat the wiring
function useFilterOptions(filterKey) {
  const dispatch = useDispatch();
  const state = useSelector(
    (s) => s.workflowDashboard.filterOptions[filterKey] || {
      options: [], loading: false, hasMore: true, pageNumber: 1, pageSize: 10, search: "",
    }
  );

  useEffect(() => {
    dispatch(fetchFilterOptions({ filterKey, search: "", page_number: 1, page_size: 10 }));
  }, [dispatch, filterKey]);

  const onSearch = (value) => {
    dispatch(setFilterOptionSearch({ filterKey, search: value }));
    dispatch(fetchFilterOptions({ filterKey, search: value, page_number: 1, page_size: state.pageSize }));
  };

  const onLoadMore = () => {
    if (!state.hasMore || state.loading) return;
    dispatch(fetchFilterOptions({
      filterKey, search: state.search, page_number: state.pageNumber + 1, page_size: state.pageSize,
    }));
  };

  return { ...state, onSearch, onLoadMore };
}


const FIELD_HEIGHT = 30; // shared height for all inputs/dropdowns in this section

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: 13,
    borderRadius: 1.5,
    height: FIELD_HEIGHT,
  },
};

// If MultiSelectDropdown forwards `sx` to its root element, use this too:
const dropdownSx = {
  "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
    fontSize: 13,
    borderRadius: 1.5,
    minHeight: FIELD_HEIGHT,
    height: FIELD_HEIGHT,
  },
};

const initialFilters = {
  speciality: [],
  coder: [],
  principalDiagnosis: [],
  drg: [],
  healthFund: [],
  episodeStatus: [],
  admissionFrom: "",
  admissionTo: "",
  separationFrom: "",
  separationTo: "",
  los: "",
  losOp: "",
  episodeId: [],
  auditRuleCategory: [],
};

export default function GlobalFilterModal({ open, onClose, onApply }) {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialFilters);
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const episodeId = useFilterOptions("episodeId");
  const healthFund = useFilterOptions("healthFund");
  const speciality = useFilterOptions("speciality");
  const coder = useFilterOptions("coder");
  const principalDiagnosis = useFilterOptions("principalDiagnosis");
  const drg = useFilterOptions("drg");
  const auditRuleCategory = useFilterOptions("auditRuleCategory");
  const status = useFilterOptions("status");

  const selectedChips = buildSelectedChips(filters);

  const handleRemoveChip = (chip) => {
    if (chip.isRange) {
      if (chip.groupKey === "admissionDate")
        setFilters((prev) => ({ ...prev, admissionFrom: "", admissionTo: "" }));
      if (chip.groupKey === "separationDate")
        setFilters((prev) => ({ ...prev, separationFrom: "", separationTo: "" }));
      if (chip.groupKey === "los")
        setFilters((prev) => ({ ...prev, losOp: "", los: "" }));
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [chip.groupKey]: prev[chip.groupKey].filter((v) => v !== chip.itemValue),
    }));
  };

  const setField = (key) => (e) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  // shared handler for MultiSelectDropdown fields (value is an array)
  const handleMultiSelect = (key) => (value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleClearAll = () => {
    setFilters(initialFilters);
  };
  const handleLosOperatorChange = (value) => {
  setFilters((prev) => ({ ...prev, losOp: value }));
};
// GlobalFilterModal.jsx
const currentFilters = useSelector((s) => s.workflowDashboard.filters);

const handleApply = () => {
  const mapped = mapFiltersToPayload(filters);

  // Preserve fields the filter modal doesn't control (file selection,
  // financial impact toggle) by keeping whatever's already in Redux for them.
  const payload = {
    ...mapped,
    file_name: currentFilters.file_name,
    financial_impact: currentFilters.financial_impact,
  };

  // dispatch(setUiFilters(filters));
  dispatch(setDashboardFilters(payload));
  dispatch(fetchWorkflowDashboard(payload));
  onClose?.();
};

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: TEXT_HEADING }}
            >
              Global Filters
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: TEXT_MUTED }}>
              Refine dashboard analytics and insight data using global filters.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
          {selectedChips.length > 0 && (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <SectionLabel>SELECTED FILTERS ({selectedChips.length})</SectionLabel>
        <Typography
          onClick={handleClearAll}
          sx={{ fontSize: 11.5, color: BRAND_BLUE, cursor: "pointer", fontWeight: 600 }}
        >
          Clear All
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75,maxHeight:'85px',overflow:'auto' }}>
        {selectedChips.map((chip, idx) => (
          <Chip
            key={`${chip.groupKey}-${idx}`}
            label={chip.text}
            size="small"
            onDelete={() => handleRemoveChip(chip)}
            sx={{
              bgcolor: "#EFF6FF",
              border: "1px solid #DBEAFE",
              color: "#1E293B",
              fontSize: 12,
              "& .MuiChip-deleteIcon": { color: "#64748B", fontSize: 16 },
            }}
          />
        ))}
      </Box>
    </Box>
  )}
        {/* Frequently Used */}
        <SectionLabel>FREQUENTLY USED</SectionLabel>

        {/* <Box sx={{ mb: 2 }}>
          <FieldLabel>Date Range</FieldLabel>
          <TextField
            type="date"
            fullWidth
            size="small"
            placeholder="Select date range"
            value={filters.dateRange}
            onChange={setField("dateRange")}
            sx={fieldSx}
            // InputProps={{
            //   endAdornment: <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: TEXT_MUTED }} />,
            // }}
          />
        </Box> */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, 
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {/* <Box>
            <FieldLabel>Facility</FieldLabel>
            <MultiSelectDropdown
              label=""
              placeholder="Search Facility"
              options={facilityOptions}
              value={filters.facility}
              onChange={handleMultiSelect("facility")}
            />
          </Box> */}
          <Box>
            <FieldLabel>Case Mix Specialty</FieldLabel>
              <MultiSelectDropdown
         placeholder="Search Specialty"
  options={speciality.options}
  loading={speciality.pageNumber === 1 && speciality.loading}
  loadingMore={speciality.pageNumber > 1 && speciality.loading}
  hasMore={speciality.hasMore}
  onLoadMore={speciality.onLoadMore}
  onInputChange={(e, newInputValue) => speciality.onSearch(newInputValue)}
  value={filters.speciality}
  onChange={handleMultiSelect("speciality")}
/>
          </Box>
          <Box>
            <FieldLabel>Coder / Audited By</FieldLabel>
               <MultiSelectDropdown
         placeholder="Search Coder"
  options={coder.options}
  loading={coder.pageNumber === 1 && coder.loading}
  loadingMore={coder.pageNumber > 1 && coder.loading}
  hasMore={coder.hasMore}
  onLoadMore={coder.onLoadMore}
  onInputChange={(e, newInputValue) => coder.onSearch(newInputValue)}
  value={filters.coder}
  onChange={handleMultiSelect("coder")}
/>
          </Box>
        </Box>

        {/* Clinical Filters */}
        <SectionLabel>CLINICAL FILTERS</SectionLabel>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
          <Box sx={{ flex: "1 1 22%", minWidth: 0 }}>
            <FieldLabel>Principal Diagnosis</FieldLabel>
             <MultiSelectDropdown
         placeholder="Search Diagnosis"
  options={principalDiagnosis.options}
  loading={principalDiagnosis.pageNumber === 1 && principalDiagnosis.loading}
  loadingMore={principalDiagnosis.pageNumber > 1 && principalDiagnosis.loading}
  hasMore={principalDiagnosis.hasMore}
  onLoadMore={principalDiagnosis.onLoadMore}
  onInputChange={(e, newInputValue) => principalDiagnosis.onSearch(newInputValue)}
  value={filters.principalDiagnosis}
  onChange={handleMultiSelect("principalDiagnosis")}
/>
          </Box>
          <Box sx={{ flex: "1 1 22%", minWidth: 0 }}>
            <FieldLabel>DRG</FieldLabel>
           <MultiSelectDropdown
    placeholder="Select DRG"
    options={drg.options}
    loading={drg.pageNumber === 1 && drg.loading}
    loadingMore={drg.pageNumber > 1 && drg.loading}
    hasMore={drg.hasMore}
    onLoadMore={drg.onLoadMore}
    onInputChange={(e, newInputValue) => drg.onSearch(newInputValue)}
    value={filters.drg}
    onChange={handleMultiSelect("drg")}
  />
          </Box>
         <Box sx={{ flex: "1 1 22%", minWidth: 0 }}>
  <FieldLabel>Health Fund</FieldLabel>
  <MultiSelectDropdown
    placeholder="Select fund"
    options={healthFund.options}
    loading={healthFund.pageNumber === 1 && healthFund.loading}
    loadingMore={healthFund.pageNumber > 1 && healthFund.loading}
    hasMore={healthFund.hasMore}
    onLoadMore={healthFund.onLoadMore}
    onInputChange={(e, newInputValue) => healthFund.onSearch(newInputValue)}
    value={filters.healthFund}
    onChange={handleMultiSelect("healthFund")}
  />
</Box>
          <Box sx={{ flex: "1 1 22%", minWidth: 0 }}>
            <FieldLabel>Episode Status</FieldLabel>
           <MultiSelectDropdown
  placeholder="Select status"
  options={status.options}
  loading={status.pageNumber === 1 && status.loading}
  loadingMore={status.pageNumber > 1 && status.loading}
  hasMore={status.hasMore}
  onLoadMore={status.onLoadMore}
  onInputChange={(e, newInputValue) => status.onSearch(newInputValue)}
  value={filters.episodeStatus}
  onChange={handleMultiSelect("episodeStatus")}
/>
          </Box>
        </Box>

        {/* Date Filters */}
<SectionLabel>DATE FILTERS</SectionLabel>
<Box sx={{ display: "flex", gap: 3, mb: 2.5, flexWrap: "wrap" }}>
  <Box sx={{ flex: "1 1 45%" }}>
    <FieldLabel>Admission Date</FieldLabel>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <TextField
        type="date"
        size="small"
        placeholder="mm/dd/yyyy"
        value={filters.admissionFrom}
        onChange={setField("admissionFrom")}
        sx={{ ...fieldSx, flex: 1 }}
        InputProps={{
          inputProps: {
            max: new Date().toISOString().split('T')[0], // Disable future dates
          }
        }}
      />
      <Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>
        to
      </Typography>
      <Tooltip
        title={!filters.admissionFrom ? "Please select a 'From' date first" : ""}
        arrow
        disableHoverListener={!!filters.admissionFrom}
      >
      <TextField
        type="date"
        size="small"
        placeholder="mm/dd/yyyy"
        value={filters.admissionTo}
        onChange={setField("admissionTo")}
        disabled={!filters.admissionFrom}
        sx={{
          ...fieldSx, 
          flex: 1,
          "& .MuiInputBase-root.Mui-disabled": {
            backgroundColor: "#f5f5f5",
            cursor: "not-allowed",
          },
        }}
        InputProps={{
          inputProps: {
            min: filters.admissionFrom || undefined, // Disable dates before "From"
            max: new Date().toISOString().split('T')[0], // Disable future dates
          }
        }}
      />
      </Tooltip>
    </Box>
  </Box>
  <Box sx={{ flex: "1 1 45%" }}>
    <FieldLabel>Separation Date</FieldLabel>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <TextField
        type="date"
        size="small"
        placeholder="mm/dd/yyyy"
        value={filters.separationFrom}
        onChange={setField("separationFrom")}
        sx={{ ...fieldSx, flex: 1 }}
        InputProps={{
          inputProps: {
            max: new Date().toISOString().split('T')[0], // Disable future dates
          }
        }}
      />
      <Typography sx={{ fontSize: 12, color: TEXT_MUTED }}>
        to
      </Typography>
        <Tooltip
        title={!filters.separationFrom ? "Please select a 'From' date first" : ""}
        arrow
        disableHoverListener={!!filters.separationFrom}
      >
      <TextField
        type="date"
        size="small"
        placeholder="mm/dd/yyyy"
        value={filters.separationTo}
        onChange={setField("separationTo")}
        disabled={!filters.separationFrom}
        sx={{
          ...fieldSx, 
          flex: 1,
          "& .MuiInputBase-root.Mui-disabled": {
            backgroundColor: "#f5f5f5",
            cursor: "not-allowed",
          },
        }}
        InputProps={{
          inputProps: {
            min: filters.separationFrom || undefined, // Disable dates before "From"
            max: new Date().toISOString().split('T')[0], // Disable future dates
          }
        }}
      />
      </Tooltip>
    </Box>
  </Box>
</Box>

        {/* Advanced Filters (collapsible) */}
        <Box
          sx={{
            bgcolor: "#EFF6FF",
            border: "1px solid #DBEAFE",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => setAdvancedOpen((o) => !o)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              cursor: "pointer",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneIcon sx={{ fontSize: 16, color: BRAND_BLUE }} />
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 700, color: BRAND_BLUE }}
              >
                Advanced Filters
              </Typography>
            </Box>
            {advancedOpen ? (
              <ExpandLessIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
            )}
          </Box>

        {/* Advanced Filters (collapsible) */}

<Collapse in={advancedOpen}>
  <Box sx={{ px: 1.5, pb: 1.5 }}>
    {/* Row 1: LOS - operator + input together */}
    <Box sx={{ mb: 1.5 }}>
      <FieldLabel>Length of Stay (LOS)</FieldLabel>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: "0 0 110px" }}>
          <MultiSelectDropdown
            multiple={false}
            label=""
            placeholder="Operator"
            options={operatorOptions}
            value={filters.losOp}
            onChange={handleLosOperatorChange}
            sx={dropdownSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter days"
            value={filters.los}
            onChange={setField("los")}
            disabled={!filters.losOp || filters.losOp.length === 0}
            sx={{
              ...fieldSx,
              bgcolor: !filters.losOp || filters.losOp.length === 0 ? "#f5f5f5" : "#fff",
              "& .MuiInputBase-root.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
              },
            }}
          />
        </Box>
      </Box>
    </Box>

    {/* Row 2: Audit Rule Category + Episode ID together */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        gap: 1.5,
      }}
    >
      <Box>
        <FieldLabel>Audit Rule Category</FieldLabel>
        <MultiSelectDropdown
         placeholder="Search Category"
  options={auditRuleCategory.options}
  loading={auditRuleCategory.pageNumber === 1 && auditRuleCategory.loading}
  loadingMore={auditRuleCategory.pageNumber > 1 && auditRuleCategory.loading}
  hasMore={auditRuleCategory.hasMore}
  onLoadMore={auditRuleCategory.onLoadMore}
  onInputChange={(e, newInputValue) => auditRuleCategory.onSearch(newInputValue)}
  value={filters.auditRuleCategory}
  onChange={handleMultiSelect("auditRuleCategory")}
/>
      </Box>
      <Box>
        <FieldLabel>Episode ID</FieldLabel>
        <MultiSelectDropdown
         placeholder="Search ID"
  options={episodeId.options}
  loading={episodeId.pageNumber === 1 && episodeId.loading}
  loadingMore={episodeId.pageNumber > 1 && episodeId.loading}
  hasMore={episodeId.hasMore}
  onLoadMore={episodeId.onLoadMore}
  onInputChange={(e, newInputValue) => episodeId.onSearch(newInputValue)}
  value={filters.episodeId}
  onChange={handleMultiSelect("episodeId")}
/>
      </Box>
    </Box>
  </Box>
</Collapse>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          onClick={handleClearAll}
          sx={{ textTransform: "none", fontWeight: 600, color: "#DC2626" }}
        >
          Clear All
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#d5dae2",
              color: "#334155",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: BRAND_BLUE,
              boxShadow: "none",
              "&:hover": { bgcolor: "#0D47A1", boxShadow: "none" },
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
